export const completionPercentage = (
  numberOfCompletedValues,
  totalRequiredValues,
) => {
  return Math.round((numberOfCompletedValues / totalRequiredValues) * 100);
};

/**
 * Creates a unique ID for form values by combining section name and item ID
 * @param {string} sectionName - The name of the section (e.g., 'trusts', 'currentJobs')
 * @param {string} id - The unique identifier for the item
 * @returns {string} - The combined ID in the format `sectionName.id`
 */
export const createFormId = (sectionName, id) => `${sectionName}.${id}`;

/**
 * Maps a field to include a unique ID based on section name and item ID
 * @param {string} sectionName - The name of the section
 * @param {string} id - The unique identifier for the item
 * @param {Object} field - The field object to be mapped
 * @returns {Object} - The field with an added ID property
 */
export const mapIdToFieldForFormValues = (sectionName, id, field) => {
  const formId = createFormId(sectionName, id);
  return {
    ...field,
    formId,
    id: field.name ? createFormId(formId, field.name) : field.id,
  };
};

/**
 * Consolidates trigger functions from multiple forms into a single trigger function.
 * @param {Object} formsRegistration - An object containing registered forms and their props.
 * @param additionalTriggers
 * @returns {Function} - A function that triggers validation for all registered forms.
 */
export const consolidateTriggers =
  (formsRegistration, additionalTriggers) => async () => {
    const forms = Object.values(formsRegistration || {});
    for (const form of forms) {
      if (typeof form.trigger === 'function') {
        await form.trigger();
      }
    }

    if (Array.isArray(additionalTriggers) && additionalTriggers.length > 0) {
      for (const trigger of additionalTriggers) {
        if (typeof trigger === 'function') {
          await trigger();
        }
      }
    }
  };

/**
 * Consolidates error objects from multiple forms into a single error object.
 * @param {Object} formsRegistration - An object containing registered forms and their props.
 * @param additionalErrors
 * @returns {Object} - A consolidated error object.
 */
export const consolidateErrors = (formsRegistration, additionalErrors) => {
  const errors = {};

  const merge = (newErrors) => {
    Object.entries(newErrors || {}).forEach(([key, value]) => {
      if (
        errors[key] &&
        typeof errors[key] === 'object' &&
        typeof value === 'object' &&
        !value.type
      ) {
        errors[key] = { ...errors[key], ...value };
      } else {
        errors[key] = value;
      }
    });
  };

  Object.values(formsRegistration || {}).forEach((f) => {
    merge(f.errors);
  });

  Object.values(additionalErrors || {}).forEach((f) => {
    merge(f);
  });

  return errors;
};
